/** @jsxImportSource @emotion/react */

import { Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import TabsNavigation from 'components/TabsNavigation/TabsNavigation'
import { TabsItem } from 'types/tabsItem'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { PlatformSlug } from 'constants/consts'
import { useAuthContext } from 'utils/auth'
import NotFound from 'components/NotFound'
import * as classes from './styles'
import { ProductPricing } from './ProductPricing'
import { ComparisonTable } from './ComparisonTable'
import { RealStudentReviews, REAL_STUDENT_REVIEWS_TAB } from './RealStudentReviews'
import { isRealStudentReviewsAvailable, isUpgradePageAvailable } from './platformConfig'

const PRODUCT_PRICING_TAB = {
  id: 'product-pricing',
  title: 'Product Pricing',
}

const COMPARISON_TABLE_TAB = {
  id: 'comparison-table',
  title: 'Comparison Table',
}

const getTabs = (platformSlug: PlatformSlug, isSuperAdmin: boolean) => {
  const isRealStudentReviewsEnabled = isRealStudentReviewsAvailable(platformSlug)

  if (!isSuperAdmin) {
    return isRealStudentReviewsEnabled ? [REAL_STUDENT_REVIEWS_TAB] : []
  }

  const tabs = [PRODUCT_PRICING_TAB, COMPARISON_TABLE_TAB]

  return isRealStudentReviewsEnabled ? [...tabs, REAL_STUDENT_REVIEWS_TAB] : tabs
}

type Props = {
  platform: Platform
}

export const UpgradePage = withPlatformCheck(({ platform }: Props) => {
  const { isSuperAdmin } = useAuthContext()
  const platformSlug = platform.slug as PlatformSlug
  const tabs = useMemo(() => getTabs(platformSlug, isSuperAdmin), [platformSlug, isSuperAdmin])
  const [activeTab, setActiveTab] = useState<TabsItem | null>(tabs[0] ?? null)

  const resolvedActiveTab = tabs.find((tab) => tab.id === activeTab?.id) ?? tabs[0]

  const renderTab = () => {
    if (!resolvedActiveTab) {
      return null
    }

    if (resolvedActiveTab.id === COMPARISON_TABLE_TAB.id) {
      return <ComparisonTable />
    }

    if (resolvedActiveTab.id === REAL_STUDENT_REVIEWS_TAB.id) {
      return <RealStudentReviews platform={platform} />
    }

    return <ProductPricing isForNewUsers />
  }

  if (!isUpgradePageAvailable(platformSlug) || !tabs.length || !resolvedActiveTab) {
    return <NotFound />
  }

  return (
    <section css={classes.container}>
      <Typography css={classes.header} variant="title3">
        Upgrade CMS
      </Typography>

      <TabsNavigation css={classes.tabs} tabs={tabs} value={resolvedActiveTab} onChange={setActiveTab} />

      {renderTab()}
    </section>
  )
})
